<script setup>
import AuthorizedLayout from '@/layouts/AuthorizedLayout.vue';
</script>

<template>
    <AuthorizedLayout>
        <template v-slot:title>
            <router-link to="/" class="m-0 text-white">
                <font-awesome-icon class="display-6 me-2" :icon="['fas', 'arrow-left']" /> <span class="h1 fw-normal m-0">{{ $t('back') }}</span>
            </router-link>
        </template>
        <div class="container mt-5">
            <div class="bg-light rounded p-4 overflow-hidden">
                <div class="row">
                    <div class="col-auto">
                        <img :src="require('@/assets/user-profile.png')" width="180" class="ms-n5 mt-n5">
                    </div>
                    <div class="col-auto">
                        <p class="text-secondary mb-2">{{ $t('profile.my_profile') }}</p>
                        <h2 class="mb-0 text-white">{{ user.given_name }} <span class="fw-normal">{{ user.family_name }}</span></h2>
                    </div>
                </div>
                <div class="row gx-5">
                    <div class="col-md-6">
                        <table class="table-dark table-xs text-body mt-5 mb-3 w-100">
                            <tbody>
                                <tr>
                                    <td class="pb-2 text-white">{{ $t('profile.name') }}:</td>
                                    <td class="text-end">{{ user.family_name }}</td>
                                </tr>
                                <tr class="border-top border-secondary">
                                    <td class="py-2 text-white">{{ $t('profile.first_name') }}:</td>
                                    <td class="text-end">{{ user.given_name }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="col-md-6">
                        <table class="table-dark table-xs text-body mt-5 mb-3 w-100">
                            <tbody>
                                <tr>
                                    <td class="pb-2 text-white">{{ $t('profile.street_number') }}:</td>
                                    <td class="text-end">{{ user.address.street_address }}</td>
                                </tr>
                                <tr class="border-top border-secondary">
                                    <td class="py-2 text-white">{{ $t('profile.zip_city') }}:</td>
                                    <td class="text-end">{{ user.address.postal_code }}, {{ user.address.locality }}</td>
                                </tr>
                                <tr class="border-top border-secondary">
                                    <td class="pt-2 text-white">{{ $t('profile.country') }}:</td>
                                    <td class="text-end">{{ user.address.country }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <!-- NOTE: Not implemented in API yet -->
                    <div class="text-end mt-4 d-none">
                        <a href="#" data-bs-toggle="modal" data-bs-target="#updatePassword" class="btn btn-outline-primary me-3">{{ $t('profile.change_password') }}</a>
                        <a href="#" data-bs-toggle="modal" data-bs-target="#updateInfo" class="btn btn-outline-primary">{{ $t('profile.edit_info') }}</a>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal fade" id="updatePassword" tabindex="-1" data-bs-backdrop="true" aria-labelledby="updatePasswordLabel" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    <div class="modal-body">
                        <h1>Change password</h1>
                        <Form @submit="updatePassword" :validation-schema="updatePasswordSchema">
                            <fieldset>
                                <label for="old_password" class="form-label"><small>Current password*</small></label>
                                <div class="mb-3">
                                    <Field type="password" class="form-control" id="old_password" name="old_password" />
                                    <ErrorMessage name="old_password" class="text-danger d-inline-block mt-1" />
                                </div>
                                <hr>
                                <label for="new_password" class="form-label"><small>New password*</small></label>
                                <div class="mb-3">
                                    <Field type="password" class="form-control" id="new_password" name="new_password" />
                                    <ErrorMessage name="new_password" class="text-danger d-inline-block mt-1" />
                                </div>
                                <label for="new_password_repeat" class="form-label"><small>Repeat new password*</small></label>
                                <div class="mb-3">
                                    <Field type="password" class="form-control" id="new_password_repeat" name="new_password_repeat" />
                                    <ErrorMessage name="new_password_repeat" class="text-danger d-inline-block mt-1" />
                                </div>
                                
                                <button class="btn btn-primary mt-4" type="submit">Confirm</button>
                            </fieldset>
                            <div class="state position-absolute start-0 top-0 end-0 bottom-0 d-flex justify-content-center align-items-center">
                                <div class="loading text-center position-absolute">
                                    <div class="icon display-1 mb-3">
                                        <font-awesome-icon class="bg-white" :icon="['fas', 'circle-notch']" size="2xl" spin spin-reverse/>
                                    </div>
                                </div>
                                <div class="success-message text-center position-absolute">
                                    <div class="icon display-1 mb-3">
                                        <font-awesome-icon class="text-success bg-white" :icon="['fas', 'circle-check']" size="2xl"/>
                                    </div>
                                    <div class="message">
                                        Your data has been successfully changed.
                                    </div>
                                </div>
                                <div class="failure-message text-center position-absolute">
                                    <div class="icon display-1 mb-3">
                                        <font-awesome-icon class="text-danger bg-white" :icon="['fas', 'circle-xmark']" size="2xl"/>
                                    </div>
                                    <div class="message">
                                        There was an error. Please try again later or contact support.
                                    </div>
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal fade" id="updateInfo" tabindex="-1" data-bs-backdrop="true" aria-labelledby="updateInfoLabel" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    <div class="modal-body">
                        <h1 class="text-dark">Edit info</h1>
                        <Form @submit="updateInfos" :validation-schema="schema">
                            <fieldset>
                                <label for="surname" class="form-label"><small>Name*</small></label>
                                <div class="mb-3">
                                    <Field type="text" class="form-control" id="surname" name="surname" :value="user.surname" />
                                    <ErrorMessage name="surname" class="text-danger d-inline-block mt-1" />
                                </div>
                                <label for="prename" class="form-label"><small>First name*</small></label>
                                <div class="mb-3">
                                    <Field type="text" class="form-control" id="prename" name="prename" :value="user.prename" />
                                    <ErrorMessage name="prename" class="text-danger d-inline-block mt-1" />
                                </div>
                                <label for="address" class="form-label"><small>Address*</small></label>
                                <div class="mb-3">
                                    <Field type="text" class="form-control" id="customer_address" name="address" />
                                    <ErrorMessage name="address" class="text-danger d-inline-block mt-1" />
                                </div>
                                <label for="postcode" class="form-label"><small>Zip code*</small></label>
                                <div class="mb-3">
                                    <Field type="text" class="form-control" id="customer_postcode" name="zip" />
                                    <ErrorMessage name="zip" class="text-danger d-inline-block mt-1" />
                                </div>
                                <label for="city" class="form-label"><small>City*</small></label>
                                <div class="mb-3">
                                    <Field type="text" class="form-control" id="customer_city" name="city" />
                                    <ErrorMessage name="city" class="text-danger d-inline-block mt-1" />
                                </div>
                                <label for="country" class="form-label"><small>Country*</small></label>
                                <div class="mb-3">
                                    <Field type="text" class="form-control" id="customer_country" name="country" />
                                    <ErrorMessage name="country" class="text-danger d-inline-block mt-1" />
                                </div>
                                <button class="btn btn-primary mt-4" type="submit">Confirm</button>
                            </fieldset>
                            <div class="state position-absolute start-0 top-0 end-0 bottom-0 d-flex justify-content-center align-items-center">
                                <div class="loading text-center position-absolute">
                                    <div class="icon display-1 mb-3">
                                        <font-awesome-icon class="bg-white" :icon="['fas', 'circle-notch']" size="2xl" spin spin-reverse/>
                                    </div>
                                </div>
                                <div class="success-message text-center position-absolute">
                                    <div class="icon display-1 mb-3">
                                        <font-awesome-icon class="text-success bg-white" :icon="['fas', 'circle-check']" size="2xl"/>
                                    </div>
                                    <div class="message">
                                        Your data has been successfully changed.
                                    </div>
                                </div>
                                <div class="failure-message text-center position-absolute">
                                    <div class="icon display-1 mb-3">
                                        <font-awesome-icon class="text-danger bg-white" :icon="['fas', 'circle-xmark']" size="2xl"/>
                                    </div>
                                    <div class="message">
                                        There was an error. Please try again later or contact support.
                                    </div>
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
        </div>
    </AuthorizedLayout>
</template>

<script>
import { useUserStore } from '@/js/user.js';
import { ref } from 'vue';
import { Form, Field, ErrorMessage, configure, ValidationProvider } from 'vee-validate';
import * as yup from 'yup';

configure({
  validateOnBlur: false, // controls if `blur` events should trigger validation with `handleChange` handler
  validateOnChange: false, // controls if `change` events should trigger validation with `handleChange` handler
  validateOnInput: false, // controls if `input` events should trigger validation with `handleChange` handler
  validateOnModelUpdate: false, // controls if `update:modelValue` events should trigger validation with `handleChange` handler
});

const schema = yup.object({
    surname: yup.string().required('Please enter a name.'),
    prename: yup.string().required('Please enter a name.'),
    address: yup.string().required('Please enter a address.'),
    zip: yup.string().required('Please enter a zip code.'),
    country: yup.string().required('Please enter a country.'),
    city: yup.string().required('Please enter a city.'),
    wallet: yup.string().required('This field is required.').matches(
        /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,39}$/,
        "Please enter a valid wallet address."
    )
});

const updatePasswordSchema = yup.object({
    old_password: yup.string().required('Please enter the current password.').test('test-name', 'fail', function(value = "404") {
        const { path, createError } = this;
        if(value == "404")
            return false;
        
            return true;
    }),
    new_password: yup.string().required('Please enter the new password.').matches(
        /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*[\]{}()?"\\,><':;|_~`=+-])[a-zA-Z\d!@#$%^&*[\]{}()?"\\,><':;|_~`=+-]{8,}$/,
        'Must contain at least 8 characters, 1 uppercase, 1 special Character, and 1 number.'
    ),
    new_password_repeat: yup.string().required('Please repeat the password.').oneOf([yup.ref('new_password')], 'Passwords must match.')
});

export default { 
    data() {
        return {
            user: null,
            censoredWallet: null,
            showWallet: ref(false),
            surname: ''
        }
    },
    methods: {
        async updateInfos(values, eventHandler) {
            let userStore = useUserStore()
            eventHandler.evt.target.classList.add('submitted')

            let personalData = {
                prename: values['prename'],
                surname: values['surname']
            }

            let customerData = {
                contact: this.user.customer[0].customer_contact,
                email: this.user.customer[0].customer_email,
                address: values['address'],
                zip: values['zip'],
                city: values['city'],
                phone: this.user.customer[0].customer_phone,
                country: values['country']
            }

            let walletData = {
                wallet_address: values['wallet']
            }

            userStore.updateData(personalData, customerData, walletData).then((response) => {  
                if(response == 200) {
                    eventHandler.evt.target.classList.add('success')
                    userStore.isAuthenticated().then((response) => {
                        this.user = userStore.$state.user
                    });
                } else
                    eventHandler.evt.target.classList.add('failure')
            }).catch(error => {

            });
        },
        async updatePassword(values, eventHandler) {
            let userStore = useUserStore()
            eventHandler.evt.target.classList.add('submitted')

            let passwordData = {
                old_password: values['old_password'],
                new_password: values['new_password']
            }

            userStore.updatePassword(passwordData).then((response) => {  
                if(response) {
                    eventHandler.evt.target.classList.add('success')
                } else {
                    eventHandler.setFieldError('old_password', 'Wrong password.');
                    eventHandler.evt.target.classList.remove('submitted');
                }
            }).catch(error => {

            });
        }
    },
    beforeMount() { 
        const user = useUserStore()
        this.user = user.$state.user
    }
}
</script>